import { useEffect, useRef } from 'react';
import type { ProviderConfigField } from '@bmai/shared';
import { resolveDefaultValueTemplate } from './resolveDefaultValueTemplate';

/**
 * For each field that declares a `defaultValueTemplate`, seed the form's
 * value when (a) the user has not edited the field and (b) every placeholder
 * the template references is filled. Re-runs whenever any referenced field's
 * value changes so the default tracks live as upstream fields change.
 *
 * Returns a `displayConfig` view of `config` with resolved defaults overlaid
 * onto fields whose current value is empty — callers pass this to renderField
 * so the user sees the default the moment upstream fields are complete,
 * without waiting for the effect to commit it to the form.
 */
export function useDefaultValueTemplates(
    fields: ProviderConfigField[],
    config: Record<string, unknown>,
    onFieldChange: (name: string, value: unknown) => void,
): Record<string, unknown> {
    const lastApplied = useRef<Record<string, string>>({});
    const signature = buildSignature(fields, config);

    useEffect(() => {
        for (const field of fields) {
            if (!field.defaultValueTemplate) continue;
            const resolved = resolveDefaultValueTemplate(field.defaultValueTemplate, config);
            if (resolved === null) continue;
            const current = config[field.name];
            const previous = lastApplied.current[field.name];
            const isEmpty = current === undefined || current === null || current === '';
            const matchesPrevious = typeof current === 'string' && current === previous;
            if ((isEmpty || matchesPrevious) && resolved !== current) {
                lastApplied.current[field.name] = resolved;
                onFieldChange(field.name, resolved);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    const hasTemplate = fields.some((f) => f.defaultValueTemplate);
    if (!hasTemplate) return config;

    const overlay: Record<string, unknown> = { ...config };
    for (const field of fields) {
        if (!field.defaultValueTemplate) continue;
        const current = overlay[field.name];
        if (current !== undefined && current !== null && current !== '') continue;
        const resolved = resolveDefaultValueTemplate(field.defaultValueTemplate, config);
        if (resolved !== null) overlay[field.name] = resolved;
    }
    return overlay;
}

function buildSignature(fields: ProviderConfigField[], config: Record<string, unknown>): string {
    const referenced = new Set<string>();
    for (const field of fields) {
        const template = field.defaultValueTemplate;
        if (!template) continue;
        referenced.add(field.name);
        const re = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(template)) !== null) {
            if (m[1]) referenced.add(m[1]);
        }
    }
    const parts: string[] = [];
    for (const name of Array.from(referenced).sort()) {
        const v = config[name];
        parts.push(`${name}=${typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v)}`);
    }
    return parts.join('|');
}
